import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Alert, AlertTitle, Box } from '@mui/material';

import type { Translate } from '@shared/hooks/locale';

type Props = {
  // Human label of the card being guarded, shown in the fallback.
  name: string;
  t: Translate;
  children: ReactNode;
};

type State = {
  error: Error | null;
  componentStack: string | null;
};

// Isolates a single Activity-tab card so a render error in one card (e.g. a
// malformed comparable listing) degrades to an inline error instead of
// white-screening the whole tab. This is internal CRM UI, so we surface the
// full error + component stack (see .cursor/rules/no-obfuscated-errors) to make
// a support screenshot actionable.
export class ActivityCardBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    console.error(`[ActivityCardBoundary] ${this.props.name} crashed`, error);
  }

  render() {
    const { error, componentStack } = this.state;
    const { children, name, t } = this.props;
    if (error == null) {
      return children;
    }
    return (
      <Box sx={{ px: { xs: 2, md: 0 }, my: 1.5 }}>
        <Alert
          severity="error"
          sx={{ '& .MuiAlert-message': { width: '100%' } }}
        >
          <AlertTitle>{t('{{name}} failed to render', { name })}</AlertTitle>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 12,
              fontFamily: 'monospace',
            }}
          >
            {[error.message, error.stack, componentStack]
              .filter(Boolean)
              .join('\n\n')}
          </Box>
        </Alert>
      </Box>
    );
  }
}
